import * as Sentry from "@sentry/react";
import { isRecordableUrl } from "./scrubUrl";

/**
 * @file
 * Two guards, dependent on each other:
 *
 *   1. THE ADDRESS. While the page address carries an identifier (`isRecordableUrl`), nothing is recorded.
 *   2. THE RECORDING MODE. The replay is kept in buffer mode, so that (1) can work at all.
 *
 * Why (2): in buffer mode the replay holds the last ~60s in memory and sends them on an error; `stop()`
 * discards that buffer. But once an error flushed it, Sentry converts the replay to continuous mode and
 * uploads segments as they're produced — `stop()` can then no longer retract what's gone. So the
 * recording is put back into buffer mode after each error, and a stored session that would resume
 * continuous is discarded before `Sentry.init()`.
 *
 * Needs `minReplayDuration: 0`: `stop()` clears the stored session, so the SDK's default 5s minimum
 * would drop the replay of an error following within five seconds of the previous one.
 */

/** The Replay SDK's own sessionStorage key (`REPLAY_SESSION_KEY`), not part of its public API. */
export const REPLAY_SESSION_STORAGE_KEY = "sentryReplaySession";

/**
 * Call before `Sentry.init()`. A stored session resumes continuous unless still unflushed buffer, and
 * init snapshots the page before any guard runs; the force-flush of a continuous `stop()` would then
 * upload that snapshot, address included. On a non-recordable address any stored session goes.
 */
export const discardUnsafeReplaySession = (): void => {
    try {
        const stored = sessionStorage.getItem(REPLAY_SESSION_STORAGE_KEY);
        if (!stored) return;

        const { sampled, segmentId } = JSON.parse(stored) as { sampled?: unknown; segmentId?: unknown };
        const resumesBuffered = sampled === "buffer" && !segmentId;
        if (resumesBuffered && isRecordableUrl(window.location.href)) return;

        sessionStorage.removeItem(REPLAY_SESSION_STORAGE_KEY);
    } catch {
        // Storage blocked: the SDK can't resume a session either. Unparsable: the SDK ignores it too.
    }
};

const CONVERSION_POLL_MS = 250;
const CONVERSION_GIVE_UP_MS = 300000;

/**
 * A continuous-mode `stop()` disables the replay at once but tears down buffer and session only after its
 * flush; a `startBuffering()` in between would be torn down with it, leaving replay enabled without a buffer.
 */
let pendingStop: Promise<void> | undefined;

/** Stops, then re-judges the address: it may have changed while the stop was in flight. */
const stopAndReapply = (replay: NonNullable<ReturnType<typeof Sentry.getReplay>>): void => {
    pendingStop = replay.stop().then(() => {
        pendingStop = undefined;
        applyReplayGuardToCurrentUrl();
    });
};

/**
 * Call on every client-side navigation. `startBuffering()` returns early while a recording runs — fine, since
 * mid-document the only way into continuous mode is the post-error conversion, which `guardReplay` watches, and
 * `discardUnsafeReplaySession` keeps a stored continuous session from resuming. While a stop is pending, its
 * re-judgement decides. Only a running replay is stopped, or the re-judgement would loop.
 */
export const applyReplayGuardToCurrentUrl = (): void => {
    const replay = Sentry.getReplay();
    if (!replay || pendingStop) return;

    if (isRecordableUrl(window.location.href)) replay.startBuffering();
    else if (replay.getRecordingMode() !== undefined) stopAndReapply(replay);
};

/** Call once, directly after `Sentry.init()`, so the address is judged before anything can be sent. */
export const guardReplay = (): void => {
    let conversionTimer: ReturnType<typeof setInterval> | undefined;

    const stopWatching = () => {
        clearInterval(conversionTimer);
        conversionTimer = undefined;
    };

    /**
     * Polls rather than waiting a fixed delay: the conversion awaits a network flush before switching mode,
     * and resetting mid-flush would cut off the error replay we do want.
     */
    const restoreBufferModeAfterError = (event: { type?: string }) => {
        // Only errors (no `type`) convert the mode; transactions and replays never do.
        if (event?.type || conversionTimer !== undefined) return;

        const startedAt = Date.now();
        conversionTimer = setInterval(() => {
            const replay = Sentry.getReplay();
            const mode = replay?.getRecordingMode();

            if (!replay || mode === undefined) {
                stopWatching();
                return;
            }

            if (mode === "session") {
                stopWatching();
                stopAndReapply(replay);
                return;
            }

            if (Date.now() - startedAt >= CONVERSION_GIVE_UP_MS) stopWatching();
        }, CONVERSION_POLL_MS);
    };

    Sentry.getClient()?.on("afterSendEvent", restoreBufferModeAfterError);

    applyReplayGuardToCurrentUrl();
};
